import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Users, 
  Zap, 
  Building2, 
  FileCheck, 
  ClipboardCheck,
  MapPin
} from "lucide-react";

const LINEMAN_IMG = "https://customer-assets.emergentagent.com/job_1dfad621-fc79-4cd5-b25a-5a11bf07839e/artifacts/ubednh4k_ChatGPT%20Image%20Feb%2022%2C%202026%2C%2002_14_10%20PM.png";
const COVERAGE_IMG = "https://images.pexels.com/photos/479356/pexels-photo-479356.jpeg?auto=compress&w=1200";

const operationalStrengths = [
  {
    icon: Users,
    title: "Multi-Crew Deployment",
    description: "Ability to deploy multiple crews simultaneously for large-scale projects."
  },
  {
    icon: Zap,
    title: "Rapid Mobilization",
    description: "Fast mobilization across state lines to meet project timelines."
  },
  {
    icon: Building2,
    title: "Multi-Carrier Experience",
    description: "Proven experience working in multi-carrier environments."
  },
  {
    icon: FileCheck,
    title: "Carrier Standards Compliance",
    description: "Full compliance with all carrier build standards and specifications."
  },
  {
    icon: ClipboardCheck,
    title: "Documentation & Reporting",
    description: "Comprehensive documentation and test report delivery."
  }
];

export default function Capabilities() {
  return (
    <div data-testid="capabilities-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#0F172A]" data-testid="capabilities-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Our Capabilities
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight text-white mt-4 mb-6">
              Operational Excellence at Scale
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Built for performance, designed for scale. Our operational capabilities 
              enable us to tackle projects of any size across the nation.
            </p>
          </div>
        </div>
      </section>

      {/* Operational Strength */}
      <section className="py-20 md:py-32 bg-white" data-testid="operational-strength">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                What Sets Us Apart
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
                Operational Strength
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Our operational capabilities are built on years of experience in 
                telecommunications construction. We bring the resources, expertise, 
                and flexibility needed to deliver on any project.
              </p>
              <img
                src={LINEMAN_IMG}
                alt="Lineman climbing wood pole working on telecom line"
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="space-y-6">
              {operationalStrengths.map((item, index) => (
                <div 
                  key={index}
                  className="flex gap-6 p-6 bg-slate-50 border-l-4 border-[#00B4D8] hover:bg-slate-100 transition-colors"
                  data-testid={`capability-${index}`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center">
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Geographic Coverage */}
      <section 
        className="py-20 md:py-32 bg-[#0F172A] relative"
        data-testid="geographic-coverage"
      >
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${COVERAGE_IMG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Where We Work
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mt-4 mb-6">
              Geographic Coverage
            </h2>
            <div className="section-line mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Primary: Ohio */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 text-center" data-testid="coverage-ohio">
              <MapPin className="h-10 w-10 text-[#00B4D8] mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-bold text-white mb-2">Ohio</h3>
              <span className="inline-block px-3 py-1 bg-[#00B4D8]/20 text-[#00B4D8] text-xs font-bold uppercase tracking-wider">
                Headquarters
              </span>
              <p className="text-slate-400 mt-4 text-sm">
                Strong operational presence with established crews and resources. Founded February 2022.
              </p>
            </div>

            {/* Nationwide */}
            <div className="bg-[#00B4D8]/20 backdrop-blur-sm border border-[#00B4D8]/40 p-8 text-center" data-testid="coverage-nationwide">
              <MapPin className="h-10 w-10 text-[#F59E0B] mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-bold text-white mb-2">Nationwide</h3>
              <span className="inline-block px-3 py-1 bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-bold uppercase tracking-wider">
                Available
              </span>
              <p className="text-slate-300 mt-4 text-sm">
                C&F Communications can mobilize and operate across the United States 
                depending on project scope and demand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#00B4D8]" data-testid="capabilities-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Leverage Our Capabilities?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Contact us to discuss how our operational strength can support your project.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-white text-[#0F172A] px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,0.3)]"
            data-testid="capabilities-cta-button"
          >
            Start a Conversation
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
